import { useEffect, ReactNode, useCallback } from 'react';
import { useGeoComply, GeoComplyFailureReason } from '@/hooks/useGeoComply';
import { useAuth } from '@/contexts/AuthContext';
import { GeoComplyCheckResult } from '@/hooks/useGeoComply';
import { useGeoComplyTiming } from '@/hooks/useGeoComplyTiming';
import { toast } from 'sonner';

/**
 * Map of failure reasons to user-friendly descriptions for toast messages
 */
const FAILURE_REASON_DESCRIPTIONS: Record<GeoComplyFailureReason, string> = {
  boundary: 'Your location is outside the permitted area',
  proxy: 'VPN or proxy detected',
  vpn: 'VPN detected',
  devtools: 'Browser developer tools or extension detected',
  location_unavailable: 'Location could not be determined',
  unknown: 'Location verification issue',
};

/**
 * Build a user-friendly description of all failure reasons
 */
function buildFailureDescription(
  reasons?: GeoComplyFailureReason[], 
  location?: GeoComplyCheckResult['location']
): string {
  const parts: string[] = [];
  
  // Add location info if available
  if (location?.country) {
    parts.push(`Detected: ${location.country}${location.region ? `, ${location.region}` : ''}`);
  }
  
  // Add failure reasons
  if (reasons && reasons.length > 0) {
    // Deduplicate vpn/proxy
    const uniqueReasons = reasons.filter((reason, index, arr) => {
      if (reason === 'vpn' && arr.includes('proxy')) return false;
      return arr.indexOf(reason) === index;
    });
    
    const reasonDescriptions = uniqueReasons
      .map(r => FAILURE_REASON_DESCRIPTIONS[r])
      .filter(Boolean);
    
    if (reasonDescriptions.length > 0) {
      parts.push(reasonDescriptions.join('; '));
    }
  }
  
  return parts.length > 0 ? parts.join('. ') : '';
}

/**
 * Get the toast title based on failure reasons
 */
function getToastTitle(reasons?: GeoComplyFailureReason[]): string {
  if (!reasons || reasons.length === 0) {
    return 'Location Check Failed';
  }
  if (reasons.includes('proxy')) {
    return 'VPN or Proxy Detected';
  }
  if (reasons.includes('devtools')) {
    return 'Browser Extension Detected';
  }
  if (reasons.includes('location_unavailable')) {
    return 'Location Unavailable';
  }
  if (reasons.includes('boundary')) {
    return 'Location Not Permitted';
  }
  return 'Location Check Failed';
}

/**
 * GeoComply Provider Component
 * 
 * This component sets up automatic periodic geolocation checks for authenticated users.
 * It ensures users are checked every 20 minutes since the last check (not fixed intervals).
 * 
 * REQUIREMENT:
 * - Check user location every 20 minutes since the last check
 * - Example: Login at 0:00 (check), gameplay at 0:05 (check), next check at 0:25 (20 mins after gameplay check)
 * - This prevents users from moving to restricted areas during their session
 * - If location check fails, show toast and logout user immediately
 * 
 * HOW IT WORKS:
 * 1. Uses useGeoComplyTiming hook to manage "20 minutes since last check" logic
 * 2. When user logs in, schedules the first periodic check
 * 3. Each check resets the timer to 20 minutes from that check
 * 4. When periodic check fails, shows toast and logs out user
 * 
 * INTEGRATION:
 * This component is added to App.tsx to wrap the entire application.
 * It runs in the background and doesn't interfere with the UI.
 * 
 * USAGE:
 * ```tsx
 * <GeoComplyProvider>
 *   <App />
 * </GeoComplyProvider>
 * ```
 * 
 * NOTE:
 * - Initial checks are performed by AuthContext on login/register
 * - Periodic checks happen 20 minutes after the last check (not fixed intervals)
 * - If check fails, toast is shown and user is logged out immediately
 */
export function GeoComplyProvider({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { isInitialized } = useGeoComply();
  const { scheduleNextCheck, resetTiming } = useGeoComplyTiming();

  /**
   * Handle periodic geolocation check result
   * 
   * This callback is called by the timing system when a periodic check completes.
   * If the check fails or location is non-compliant, shows a toast and logs out.
   */
  const handlePeriodicCheckResult = useCallback(async (
    isCompliant: boolean, 
    locationData?: GeoComplyCheckResult['location'],
    reasons?: GeoComplyFailureReason[]
  ) => {
    // Don't handle if user isn't logged in
    if (!user) return;

    // If check failed or location is non-compliant, show toast and logout
    if (!isCompliant) {
      // Build the toast message with all failure reasons
      const title = getToastTitle(reasons);
      const description = buildFailureDescription(reasons, locationData);
      
      // Show error toast
      toast.error(title, {
        description: description || 'Your session has been terminated due to location compliance.',
        duration: 5000, // Show for 5 seconds
      });
      
      // Log out immediately
      await signOut();
    }
    // If check succeeds, timing hook automatically schedules next check 20 minutes later
  }, [user, signOut]);

  /**
   * Set up periodic geolocation checks
   */
  useEffect(() => {
    // Don't set up checks if SDK isn't initialized or user isn't logged in
    if (!isInitialized || !user) {
      // Reset timing when user logs out
      if (!user) {
        resetTiming();
      }
      return;
    }
    
    // Register the callback for periodic checks
    scheduleNextCheck(handlePeriodicCheckResult);

    // Cleanup: Reset timing when component unmounts or user logs out
    return () => {
      resetTiming();
    };
  }, [isInitialized, user, scheduleNextCheck, resetTiming, handlePeriodicCheckResult]);

  // Just render children - no modal needed
  return <>{children}</>;
}

